// The world map as the Worker serves it (/api/map/<wid>?size=…; worker/map.ts): a 16-byte header, then every chunk's cells.
// Cell layout is the game's (front-server Operations/MultiTileUpdateOperation.php, the writer the hourly map refresh and
// Tasks/Map/Update use; the phone's Map/ChunkData.cs decodes the same way):
//   byte 1 = level (3 bits) | beginnerProtection | truce | barbarian | savage | reserved
//   byte 2 = 0        bytes 3-4 = guild id, big-endian 16 bits
// (The older single-tile writer, MapTileUpdateOperation.php, packed a 22-bit guild id across bytes 1–4; the live chunk
// files carry the 16-bit layout — guild ids 1–40 on World 2 — so that is what is read here.)

export interface WorldMap {
  size: number;
  chunk: number;
  n: number;
  /** n*n*chunk*chunk*4 bytes, chunk-major */
  cells: Uint8Array;
}

export interface Cell {
  x: number;
  y: number;
  /** 1–4: the city's size level (keep tier); 0 = empty cell */
  level: number;
  beginnerProtection: boolean;
  truce: boolean;
  barbarian: boolean;
  savage: boolean;
  guildId: number;
}

export function parseWorldMap(buf: ArrayBuffer): WorldMap {
  const b = new Uint8Array(buf);
  if (b.length < 16 || b[0] !== 0x56 || b[1] !== 0x4d || b[2] !== 0x41 || b[3] !== 0x50) throw new Error("not a VMAP blob");
  const dv = new DataView(buf);
  const version = dv.getUint16(4, true);
  if (version !== 1) throw new Error(`VMAP version ${version} not understood`);
  const chunk = dv.getUint16(6, true);
  const size = dv.getUint32(8, true);
  const n = dv.getUint32(12, true);
  const expected = 16 + n * n * chunk * chunk * 4;
  if (b.length !== expected) throw new Error(`VMAP size mismatch: ${b.length} bytes, expected ${expected}`);
  return { size, chunk, n, cells: b.subarray(16) };
}

/** Byte offset of a cell inside `cells`. */
export function cellOffset(m: WorldMap, x: number, y: number): number {
  const c = m.chunk;
  const cx = Math.floor(x / c);
  const cy = Math.floor(y / c);
  const tx = x - cx * c;
  const ty = y - cy * c;
  return ((cy * m.n + cx) * c * c + ty * c + tx) * 4;
}

export function decodeCell(b: Uint8Array, off: number, x: number, y: number): Cell | null {
  const b1 = b[off];
  const b2 = b[off + 1];
  const b3 = b[off + 2];
  const b4 = b[off + 3];
  if (b1 === 0 && b2 === 0 && b3 === 0 && b4 === 0) return null;
  return {
    x,
    y,
    level: (b1 >> 5) & 0x07,
    beginnerProtection: !!(b1 & 0x10),
    truce: !!(b1 & 0x08),
    barbarian: !!(b1 & 0x04),
    savage: !!(b1 & 0x02),
    guildId: (b3 << 8) | b4,
  };
}

export function cellAt(m: WorldMap, x: number, y: number): Cell | null {
  if (x < 0 || y < 0 || x >= m.size || y >= m.size) return null;
  return decodeCell(m.cells, cellOffset(m, x, y), x, y);
}

/** Every city on the map (a 1024-cell world has about 1,400). */
export function allCities(m: WorldMap): Cell[] {
  const out: Cell[] = [];
  const c = m.chunk;
  for (let ci = 0; ci < m.n * m.n; ci++) {
    const cx = ci % m.n;
    const cy = Math.floor(ci / m.n);
    const base = ci * c * c * 4;
    for (let i = 0; i < c * c; i++) {
      const off = base + i * 4;
      if (m.cells[off] === 0 && m.cells[off + 1] === 0 && m.cells[off + 2] === 0 && m.cells[off + 3] === 0) continue;
      const cell = decodeCell(m.cells, off, cx * c + (i % c), cy * c + Math.floor(i / c));
      if (cell) out.push(cell);
    }
  }
  return out;
}

/** Pack one cell the way the server does (for tests and fixtures). */
export function encodeCell(c: Omit<Cell, "x" | "y">): [number, number, number, number] {
  let b1 = (c.level & 0x07) << 5;
  if (c.beginnerProtection) b1 |= 0x10;
  if (c.truce) b1 |= 0x08;
  if (c.barbarian) b1 |= 0x04;
  if (c.savage) b1 |= 0x02;
  return [b1, 0, (c.guildId >> 8) & 0xff, c.guildId & 0xff];
}

export const cellKey = (x: number, y: number) => `${x},${y}`;

/** Every city, indexed by "x,y". */
export function cityIndex(m: WorldMap): Map<string, Cell> {
  const idx = new Map<string, Cell>();
  for (const c of allCities(m)) idx.set(cellKey(c.x, c.y), c);
  return idx;
}

/** Cities per guild id (0 = no guild), most cities first. */
export function guildCounts(cities: Cell[]): { gid: number; n: number }[] {
  const n = new Map<number, number>();
  for (const c of cities) if (!c.barbarian) n.set(c.guildId, (n.get(c.guildId) ?? 0) + 1);
  return [...n.entries()].map(([gid, k]) => ({ gid, n: k })).sort((a, b) => b.n - a.n || a.gid - b.gid);
}
